/**
 * Category icon resolution - single source of truth.
 *
 * Priority: explicit category.icon > exact name match > keyword match >
 * folder fallback. Three components used to carry their own diverging
 * copies of the exact-name map, and every custom-named category ("House",
 * "Car", "electric misc bills etc"...) fell through to the generic folder.
 *
 * Keyword matching is cosmetic - plain case-insensitive word tests on the
 * raw stored name (defaults are stored in English; ja/th display names come
 * from the catalog and never reach this matcher).
 */

// Exact stored-name matches (merged from the old per-component maps)
const EXACT_ICONS: Record<string, string> = {
  'Food & Dining': '🍽️',
  'Food': '🍔',
  'Groceries': '🛒',
  'Housing': '🏠',
  'Rent': '🏠',
  'Transportation': '🚗',
  'Transport': '🚌',
  'Entertainment': '🎬',
  'Shopping': '🛍️',
  'Utilities': '💡',
  'Bills': '📄',
  'Healthcare': '💊',
  'Health': '🏥',
  'Insurance': '🛡️',
  'Subscriptions': '📱',
  'Other': '📦',
  'Salary': '💰',
  'Income': '💵',
  'Freelance': '💻',
  'Investment': '📈',
  'Gift': '🎁',
  'Refund': '↩️',
  'Education': '📚',
};

// First match wins - keep more specific patterns before broader ones
const KEYWORD_ICONS: Array<[RegExp, string]> = [
  [/coffee|cafe|tea\b/i, '☕'],
  [/restaurant|dining|takeout|delivery/i, '🍽️'],
  [/grocer|supermarket/i, '🛒'],
  [/\bfood|snack|meal/i, '🍔'],
  [/house|home|rent|housing|mortgage|apartment|condo/i, '🏠'],
  [/electric|utilit|power|water bill|internet|wifi|phone bill|\bbills?\b/i, '💡'],
  [/\bcar\b|\bauto\b|vehicle|\bgas\b|fuel|parking|repair/i, '🚗'],
  [/uber|lyft|taxi|transit|\bbus\b|train|metro/i, '🚕'],
  [/travel|flight|hotel|\btrip\b|vacation/i, '✈️'],
  [/crypto|bitcoin|\bbtc\b|\beth\b|\bsol\b|coin|token/i, '🪙'],
  [/external|capital|withdraw/i, '🔁'],
  [/\bfx\b|forex|exchange|currency/i, '💱'],
  [/loan|\blns\b|debt|lend|borrow/i, '💸'],
  [/invest|stock|dividend|broker/i, '📈'],
  [/salary|paycheck|payroll|wage/i, '💰'],
  [/business|work|office|client/i, '💼'],
  [/insurance/i, '🛡️'],
  [/subscri|stream|netflix|spotify/i, '📱'],
  [/game|gaming|steam/i, '🎮'],
  [/movie|cinema|entertain|concert/i, '🎬'],
  [/cloth|fashion|apparel|shoe/i, '👕'],
  [/shopping|amazon|store/i, '🛍️'],
  [/gym|fitness|sport|yoga/i, '🏋️'],
  [/health|medical|doctor|dental|pharma|medicine/i, '💊'],
  [/school|college|course|educat|book|tuition/i, '📚'],
  [/\bpet\b|\bdog\b|\bcat\b|\bvet\b/i, '🐾'],
  [/kid|child|baby|daycare/i, '🍼'],
  [/gift|donat|charity/i, '🎁'],
  [/\btax\b|taxes|\bfee\b|fees/i, '🧾'],
  [/refund|cashback|rebate/i, '↩️'],
];

const FALLBACK_ICON = '📁';

/**
 * Resolve the display icon for a category.
 * @param name - Stored category name (untranslated)
 * @param icon - Explicit user-chosen icon (always wins when set)
 */
export function getCategoryIcon(name?: string, icon?: string): string {
  if (icon) return icon;
  if (!name) return FALLBACK_ICON;
  if (EXACT_ICONS[name]) return EXACT_ICONS[name];
  for (const [pattern, emoji] of KEYWORD_ICONS) {
    if (pattern.test(name)) return emoji;
  }
  return FALLBACK_ICON;
}
